import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Spinner,
  Dropdown,
  Modal,
  Form,
} from "react-bootstrap";
import { GoCheckCircleFill } from "react-icons/go";
import {
  FiEdit,
  FiEye,
  FiFileText,
  FiList,
  FiMoreHorizontal,
} from "react-icons/fi";
import { FaRegClone } from "react-icons/fa6";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

function Assessments({ refreshVersion = 0 }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { moduleId } = state || {};
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [formData, setFormData] = useState({
    assessment_id: null,
    assessment_name: "",
    information: "",
    module_id: null,
  });
  const [modules, setModules] = useState([]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_API + `/getModules`,
        );
        if (response.data.success) {
          setModules(response.data.data);
        } else {
          console.error("Error fetching modules:", response.data.message);
        }
      } catch (err) {
        console.error("Failed to fetch modules:", err);
      }
    };

    fetchModules();
  }, []);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/getAssessment?moduleId=${moduleId}`,
      );
      const data = await response.json();

      if (data.success) {
        setAssessments(data.data);
      } else {
        console.error("Error fetching assessments:", data.message);
      }
    } catch (error) {
      console.error("Error fetching assessments:", error);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments, refreshVersion]);

  const handlePreviewPage = (assessmentId) => {
    navigate("/preview", { state: { assessmentId } });
  };

  const handleAssessmentClick = (assessmentId, assessmentName) => {
    navigate("/assessment", { state: { assessmentId, assessmentName } });
  };

  const handleEdit = (assessmentId) => {
    const selectedAssessment = assessments.find(
      (assessment) => assessment.assessment_id === assessmentId,
    );
    if (selectedAssessment) {
      const nextFormData = {
        assessment_id: selectedAssessment.assessment_id,
        assessment_name: selectedAssessment.assessment_name,
        information: selectedAssessment.information || "",
        module_id: selectedAssessment.module_id,
        type: selectedAssessment.type || "",
      };
      setFormData(nextFormData);
      setOriginalFormData(nextFormData);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setOriginalFormData(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/updateAssessment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        console.error("HTTP error:", data.message);
        toast.error(
          `Failed to update assessment: ${data.message || "Unknown error"}`,
        );
        return;
      }

      if (data.success) {
        toast.success("Assessment updated successfully.");
        await fetchAssessments();
        handleCloseModal();
      } else {
        console.error("Error updating assessment:", data.message);
        toast.error(`Failed to update assessment: ${data.message}`);
      }
    } catch (error) {
      console.error("Error updating assessment:", error);
      toast.error(
        "An error occurred while updating the assessment. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  const hasChanges =
    originalFormData !== null &&
    ["assessment_name", "information", "module_id", "type"].some(
      (field) =>
        String(formData[field] ?? "") !==
        String(originalFormData[field] ?? ""),
    );
  const isFormValid =
    formData.assessment_name.trim() !== "" &&
    formData.information.trim() !== "" &&
    formData.module_id &&
    formData.type &&
    formData.type !== "Select Type";

  const handleClone = async (assessmentId) => {
    const loginUser = JSON.parse(localStorage.getItem("user"));

    setCloning(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/cloneAssessment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assessmentId,
            user_id: loginUser?.user_id || null,
            username: loginUser?.username || "Unknown",
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        toast.success("Assessment cloned successfully.");
        await fetchAssessments();
      } else {
        console.error("Error cloning assessment:", data.message);
        toast.error(data.message || "Failed to clone assessment.");
      }
    } catch (error) {
      console.error("Error cloning assessment:", error);
      toast.error("An error occurred while cloning the assessment.");
    } finally {
      setCloning(false);
    }
  };

  if (loading || cloning) {
    return (
      <div className="assessment-loading" aria-live="polite">
        <Spinner animation="border" role="status" style={{ color: "#263F86" }}>
          <span className="visually-hidden">
            {cloning ? "Cloning Assessment..." : "Loading..."}
          </span>
        </Spinner>
        <p className="mt-3 mb-0">
          {cloning ? "Creating a copy of this assessment..." : "Loading assessments..."}
        </p>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className="assessment-empty">
        <span className="assessment-empty__icon">
          <FiFileText size={26} aria-hidden="true" />
        </span>
        <h2>No assessments yet</h2>
        <p>
          Create the first assessment for this module. It will appear here with
          quick access to questions, preview, publishing, and settings.
        </p>
      </div>
    );
  }

  return (
    <section aria-labelledby="assessment-list-title">
      <div className="assessment-list__header">
        <div>
          <h2 id="assessment-list-title">Assessment library</h2>
          <p>Open questions, preview learner view, or manage an assessment.</p>
        </div>
        <span
          className="assessment-list__count"
          aria-label={`${assessments.length} assessments`}
        >
          {assessments.length}
        </span>
      </div>

      <div className="assessment-list">
        {assessments.map((assessment) => {
          const isPublished = assessment.status === "Published";

          return (
            <article className="assessment-card" key={assessment.assessment_id}>
              <div className="assessment-card__identity">
                <span className="assessment-card__icon">
                  <FiFileText size={21} aria-hidden="true" />
                </span>

                <div className="assessment-card__copy">
                  <div className="assessment-card__meta">
                    <span className="assessment-card__type">
                      {assessment.type || "Assessment"}
                    </span>
                    <span
                      className={`assessment-card__status ${
                        isPublished
                          ? "assessment-card__status--published"
                          : "assessment-card__status--draft"
                      }`}
                    >
                      <GoCheckCircleFill size={11} aria-hidden="true" />
                      {assessment.status || "Draft"}
                    </span>
                  </div>

                  <h3 title={assessment.assessment_name}>
                    {assessment.assessment_name}
                  </h3>
                  <p>
                    {assessment.information ||
                      "No assessment description has been added yet."}
                  </p>
                </div>
              </div>

              <div className="assessment-card__actions">
              <Button
                className="assessment-card__primary"
                onClick={() =>
                  handleAssessmentClick(
                    assessment.assessment_id,
                    assessment.assessment_name,
                  )
                }
              >
                <FiList size={15} aria-hidden="true" />
                <span>Questions</span>
              </Button>

                <Button
                  variant="outline-secondary"
                  className="assessment-card__secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviewPage(assessment.assessment_id);
                  }}
                >
                  <FiEye size={15} aria-hidden="true" />
                  <span>Preview</span>
                </Button>

                <Dropdown align="end" className="assessment-card__menu">
                  <Dropdown.Toggle
                    className="assessment-card__menu-button"
                    aria-label={`More actions for ${assessment.assessment_name}`}
                    title="More actions"
                  >
                    <FiMoreHorizontal size={20} aria-hidden="true" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(assessment.assessment_id);
                      }}
                    >
                      <FiEdit size={15} className="me-2" aria-hidden="true" />
                      Edit details
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        const confirmed = window.confirm(
                          "Create a copy of this assessment?",
                        );
                        if (confirmed) {
                          handleClone(assessment.assessment_id);
                        }
                      }}
                    >
                      <FaRegClone size={15} className="me-2" aria-hidden="true" />
                      Clone assessment
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </article>
          );
        })}
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        className="assessment-edit-modal"
        dialogClassName="assessment-edit-dialog"
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <span className="assessment-edit-modal__title">Edit assessment</span>
              <span className="assessment-edit-modal__subtitle">
                Update the details learners will see for this assessment.
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="assessment-edit-modal__field">
              <Form.Label>
                Assessment name <span aria-hidden="true">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="assessment_name"
                placeholder="Enter assessment name"
                value={formData.assessment_name}
                onChange={handleChange}
                maxLength={120}
                required
              />
              <Form.Text>
                Use a clear name learners can identify quickly.
              </Form.Text>
            </Form.Group>
            <Form.Group className="assessment-edit-modal__field">
              <div className="assessment-edit-modal__label-row">
                <Form.Label>
                  Information <span aria-hidden="true">*</span>
                </Form.Label>
                <span>{formData.information.length}/500</span>
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                name="information"
                placeholder="Add a short description for learners"
                value={formData.information}
                onChange={handleChange}
                maxLength={500}
                required
              />
              <Form.Text>
                Briefly explain the purpose or scope of the assessment.
              </Form.Text>
            </Form.Group>

            <div className="assessment-edit-modal__grid">
              <Form.Group>
                <Form.Label>
                  Module <span aria-hidden="true">*</span>
                </Form.Label>
                <Form.Select
                  name="module_id"
                  value={formData.module_id || ""}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select a module
                  </option>
                  {modules.map((module) => (
                    <option key={module.module_id} value={module.module_id}>
                      {module.module_name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text>Move this assessment to another module.</Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>
                  Assessment type <span aria-hidden="true">*</span>
                </Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type || ""}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select a type
                  </option>
                  <option value="Practice">Practice</option>
                  <option value="Exam">Exam</option>
                </Form.Select>
                <Form.Text>Choose how learners will take it.</Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="assessment-edit-modal__required-note">
             
            </span>
            <div className="assessment-edit-modal__footer-actions">
              <Button
                type="button"
                variant="light"
                onClick={handleCloseModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="assessment-edit-modal__save"
                disabled={loading || !hasChanges || !isFormValid}
              >
                {loading ? "Saving changes..." : "Save changes"}
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </section>
  );
}

export default Assessments;
